import { faqsPl } from "@data/backend-panels-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  mobileClusterLinks,
  mobileKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/mobile-web-landing/shared";
import {
  Calendar,
  CheckCircle,
  Cloud,
  Code,
  Database,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  PenTool,
  Rocket,
  Search,
  Server,
  ShieldCheck,
  Users,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Backend i panele administracyjne - zarządzanie danymi, użytkownikami i integracjami";

export const description =
  "Backend i panele administracyjne na zamówienie: API, dashboard, role, integracje i modernizacja legacy. Polski software house — od MVP po utrzymanie SLA.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Backend i panele administracyjne",
    description,
    "Backend and admin panel development",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Backend i panele",
  headerTail: "administracyjne",
  description:
    "Budujemy API, dashboardy i panele admina pod aplikacje mobilne i webowe. Integracje, role użytkowników, modernizacja legacy i utrzymanie — jeden zakres zamiast trzech osobnych zleceń.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "API + panel + integracje", Icon: CheckCircle },
    { text: "Przejęcie i audyt legacy", Icon: CheckCircle },
    { text: "Utrzymanie ze SLA", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo są backend i panele administracyjne?",
    p: "Panel admina, dashboard i API zwykle są rdzeniem pierwszego wydania — razem z aplikacją, nie osobno. Często też: przejęcie Node/Java i utrzymanie po innym wykonawcy.",
  },
  points: [
    {
      Icon: Rocket,
      title: "Budujesz MVP z logowaniem, dashboardem i powiadomieniami",
      description:
        "Potrzebujesz backendu i panelu od razu — bo bez nich aplikacja mobilna lub webowa nie ma sensu operacyjnego.",
    },
    {
      Icon: LayoutDashboard,
      title: "Chcesz panel admina do zarządzania użytkownikami i danymi",
      description:
        "Role, treści, raporty, konfiguracja — centrum operacyjne produktu, nie „doklejka” po premierze.",
    },
    {
      Icon: Server,
      title: "Masz legacy backend do modernizacji lub migracji",
      description:
        "Node.js, Java, stary monolit — potrzebujesz audytu, planu migracji do chmury i bezpiecznego przejęcia utrzymania.",
    },
    {
      Icon: Link2,
      title: "Potrzebujesz integracji API (CRM, płatności, ERP)",
      description:
        "Stripe, BLIK, CRM, TMS, sklep — spinamy systemy zewnętrzne z Waszym panelem i aplikacją.",
    },
    {
      Icon: LifeBuoy,
      title: "Szukasz utrzymania backendu i monitoringu",
      description:
        "Model godzinowy lub SLA, alerty, Sentry/Grafana — stabilna eksploatacja po wdrożeniu.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opisz aplikację, panel albo legacy backend — zaproponujemy zakres MVP, audytu lub utrzymania.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto budować backend i panel z nami?",
    p: "Aplikacja bez solidnego API i panelu szybko staje się kosztem. Projektujemy warstwę serwerową tak, żeby dało się ją rozwijać, integrować i utrzymać — nie tylko „żeby działało na demo”.",
    imageAlt: "Backend i panele administracyjne na zamówienie",
  },
  values: [
    {
      Icon: LayoutDashboard,
      title: "Panel admina jako część produktu",
      description:
        "Dashboard, role i operacje biznesowe projektujemy razem z API — tak, żeby panel był użyteczny od pierwszego dnia pracy zespołu.",
    },
    {
      Icon: Database,
      title: "API pod mobile i web",
      description:
        "REST/GraphQL, autoryzacja, powiadomienia i spójny model danych pod aplikację Flutter, PWA albo SaaS.",
    },
    {
      Icon: Search,
      title: "Audyt i przejęcie legacy",
      description:
        "NDA, przegląd repozytorium, ocena długu technicznego i plan dalszego developmentu bez przestoju.",
    },
    {
      Icon: Cloud,
      title: "Infrastruktura w UE",
      description:
        "AWS lub hosting zgodny z RODO — skalowanie, backupy i koszty operacyjne pod realny ruch.",
    },
    {
      Icon: ShieldCheck,
      title: "Bezpieczeństwo i role dostępu",
      description:
        "Uprawnienia, audyt działań admina i ochrona danych użytkowników — szczególnie przy panelach wieloosobowych.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Backend i panele — co budujemy",
    p: "Od API pod MVP po modernizację legacy i SLA. Zakres łączymy z aplikacją mobilną, webową albo SaaS.",
  },
  services: [
    {
      Icon: Search,
      title: "Analiza i architektura backendu",
      description:
        "Model danych, API, role, integracje i infrastruktura — plan z widełkami kosztów przed implementacją.",
    },
    {
      Icon: Server,
      title: "API i logika biznesowa",
      description:
        "Backend pod aplikację: autoryzacja, endpointy, kolejki, powiadomienia i testy integracyjne.",
    },
    {
      Icon: LayoutDashboard,
      title: "Panel administracyjny i dashboard",
      description:
        "Webowy panel: użytkownicy, treści, raporty, konfiguracja produktu i operacje dzienne zespołu.",
    },
    {
      Icon: Link2,
      title: "Integracje zewnętrzne",
      description:
        "Płatności, CRM, ERP, sklepy, webhooks — połączenie Waszego systemu z ekosystemem klienta.",
    },
    {
      Icon: Code,
      title: "Modernizacja legacy i migracja",
      description:
        "Refaktor, podział monolitów, migracja bazy i przejście na nowszą infrastrukturę bez utraty danych.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie, monitoring i SLA",
      description:
        "Alerty, poprawki, dyżury i rozwój API po wdrożeniu — partner do eksploatacji, nie tylko do buildu.",
    },
  ],
  cta: {
    h4: "Nie wiesz, od czego zacząć?",
    p: "Napisz, czy budujesz nowy panel, czy przejmujesz legacy — zaproponujemy audyt albo zakres MVP.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak powstaje backend i panel administracyjny",
    p: "Od NDA i audytu albo ustalonego zakresu MVP po wdrożenie i utrzymanie. Jasne kamienie milowe dla API i panelu.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "Omawiamy aplikację, dane, integracje i czy zaczynamy od zera, czy od przejęcia kodu.",
    },
    {
      Icon: FileText,
      title: "Brief albo audyt repozytorium",
      duration: "w kilka dni",
      description:
        "Mapujemy wymagania panelu i API albo — po NDA — oceniamy istniejący backend.",
    },
    {
      Icon: PenTool,
      title: "Architektura i wycena",
      duration: "ustalenie harmonogramu",
      description:
        "Diagram systemu, priorytety MVP, model fixed/T&M i kryteria akceptacji.",
    },
    {
      Icon: Code,
      title: "Implementacja API i panelu",
      duration: "min. 6–12 tygodni",
      description:
        "Budujemy backend i panel równolegle z aplikacją użytkownika, jeśli jest w zakresie.",
    },
    {
      Icon: Users,
      title: "Testy z operatorami",
      duration: "2–4 tygodnie",
      description:
        "Weryfikujemy role, dashboard i procesy administracyjne na realnych scenariuszach.",
    },
    {
      Icon: CheckCircle,
      title: "Wdrożenie i utrzymanie",
      duration: "na stałe",
      description:
        "Produkcja, monitoring, SLA i kolejne integracje albo przekazanie z dokumentacją.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Wyceń backend i panel",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  h3: "Polski zespół backendu, API i paneli admina",
  imageAlt: "Zespół Blues Brackets — backend i panele administracyjne",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o backend i panele",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Potrzebujesz backendu lub panelu administracyjnego?",
    p: "Umów się na darmową konsultację — opowiedz o API, dashboardzie albo legacy, a zaproponujemy zakres współpracy.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Backend i panele administracyjne — API, dashboard, integracje i utrzymanie. Polski software house.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      {
        href: "/aplikacje-mobilne-i-webowe",
        text: "Aplikacje mobilne i webowe",
      },
      ...mobileClusterLinks.filter(
        l =>
          l.href !== "/aplikacje-mobilne-i-webowe" &&
          l.href !== "/backend-i-panele-administracyjne",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...mobileKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
